import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';
import api from '../../../app/api/api';
import { handleApiError } from '../../../app/api/errorHandler';
import { RestaurantUser } from '../../../app/data/models/restaurantUsers/restaurantUser';
import { AxiosError } from 'axios';

export interface UserResendStatus {
  [key: string]: {
    isLoading: boolean;
    lastResentAt?: number;
    error?: string;
  };
}

export interface RestaurantUserListState {
  status: string;
  restaurantUsers: RestaurantUser[];
  resendStatus: UserResendStatus;
}

const initialState: RestaurantUserListState = {
  status: 'idle',
  restaurantUsers: [],
  resendStatus: {},
};

export const fetchRestaurantUserListAsync = createAsyncThunk<
  RestaurantUser[],
  { restaurantId: string }
>('restaurantUsers/list', async ({ restaurantId }, thunkApi) => {
  try {
    return await api.RestaurantUser.list(restaurantId);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const deleteRestaurantUserAsync = createAsyncThunk<
  void,
  { restaurantId: string; email: string }
>('restaurantUsers/delete', async ({ restaurantId, email }, thunkApi) => {
  try {
    await api.RestaurantUser.delete(restaurantId, email);
    thunkApi.dispatch(fetchRestaurantUserListAsync({ restaurantId }));
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkApi.rejectWithValue({ error: error.response?.data });
    }
    return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

interface ResendVerificationResponse {
  email: string;
  timestamp: number;
  success?: boolean;
  alreadyConfirmed?: boolean;
}

export const resendVerificationEmailAsync = createAsyncThunk<
  ResendVerificationResponse,
  { restaurantId: string; email: string },
  { rejectValue: { message: string } }
>(
  'restaurantUsers/resendVerificationEmail',
  async ({ restaurantId, email }, { rejectWithValue }) => {
    try {
      await api.RestaurantUser.resendVerificationEmail(restaurantId, email);
      return { email, timestamp: Date.now(), success: true };
    } catch (error: unknown) {
      // Handle API errors through our error handler
      const wasHandled = handleApiError(
        error,
        t('restaurant-users.resend-email.error')
      );

      // If the error was handled by our handler, we still want to update the UI
      if (wasHandled) {
        return {
          email,
          timestamp: Date.now(),
          success: true,
          alreadyConfirmed:
            (error instanceof AxiosError &&
              error.response?.data?.code === 'EMAIL_ALREADY_CONFIRMED') ||
            false,
        };
      }

      // For unhandled errors, reject with the error message
      return rejectWithValue({
        message:
          (error instanceof AxiosError && error.response?.data?.detail) ||
          (error instanceof Error && error.message) ||
          t('restaurant-users.resend-email.error'),
      });
    }
  }
);

export const restaurantUserListSlice = createSlice({
  name: 'restaurantUsers',
  initialState,
  reducers: {
    clearResendStatus: (state, action: { payload: string }) => {
      delete state.resendStatus[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRestaurantUserListAsync.pending, (state) => {
      state.status = 'pendingFetchRestaurantUserList';
    });
    builder.addCase(fetchRestaurantUserListAsync.fulfilled, (state, action) => {
      state.restaurantUsers = action.payload;
      state.status = 'idle';
    });
    builder.addCase(fetchRestaurantUserListAsync.rejected, (state) => {
      state.status = 'idle';
    });

    // Handle resend verification email
    builder
      .addCase(resendVerificationEmailAsync.pending, (state, action) => {
        const { email } = action.meta.arg;
        state.resendStatus[email] = {
          ...state.resendStatus[email],
          isLoading: true,
          error: undefined,
        };
      })
      .addCase(resendVerificationEmailAsync.fulfilled, (state, action) => {
        const { email, timestamp, alreadyConfirmed } = action.payload;

        // If the email was already confirmed, we need to update the user list
        if (alreadyConfirmed) {
          const userIndex = state.restaurantUsers.findIndex(
            (u) => u.email === email
          );
          if (userIndex !== -1) {
            // Create a new array with the updated user
            state.restaurantUsers = [
              ...state.restaurantUsers.slice(0, userIndex),
              { ...state.restaurantUsers[userIndex], emailConfirmed: true },
              ...state.restaurantUsers.slice(userIndex + 1),
            ];
          }
        }

        state.resendStatus[email] = {
          ...state.resendStatus[email],
          isLoading: false,
          lastResentAt: timestamp,
          error: undefined,
        };
      })
      .addCase(resendVerificationEmailAsync.rejected, (state, action) => {
        const email = action.meta.arg.email;
        state.resendStatus[email] = {
          ...state.resendStatus[email],
          isLoading: false,
          error:
            action.payload?.message || 'Failed to resend verification email',
        };
      });
  },
});
